import * as React from 'react'
import { createPortal } from 'react-dom'

import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { useClickOutside } from '@/app/hooks/useClickOutside'
import { AdminIcon } from '@/modules/shared/components'
import { useQueryCompanyRoles } from '@/lib/api/company/roles'

type Props = {
  /** Wszystkie role do wyboru. */
  available: RolePermissionModel[]
  /** Firma wiersza - dokłada do listy role własne firmy i jej konta usługowego. */
  companyId?: string
  /** Role już przypisane - klik zdejmuje, klik na liście przełącza. */
  selected: RolePermissionModel[]
  onToggle: (role: RolePermissionModel) => void
}

/**
 * Role przypisane do firmy albo grupy - pigułki plus rozwijana lista do przełączania.
 *
 * Stara wersja robiła to bootstrapowym `Popover` sterowanym mapą `popoverOpen`
 * trzymaną w stanie ekranu; tutaj każdy wiersz pilnuje się sam.
 */
export const RolePicker: React.FC<Props> = ({
  available,
  companyId,
  selected,
  onToggle,
}) => {
  const [open, setOpen] = React.useState(false)
  // Dopiero po rozwinięciu - tabela ma wiersz na każdą firmę użytkownika.
  const companyRoles = useQueryCompanyRoles(companyId, { enabled: open })
  const options = React.useMemo(() => {
    const own = (companyRoles.data?.roles ?? []).filter(
      (role) => role.ownerCompanyId != null && !available.some((item) => String(item.id) === String(role.id)),
    )
    return [...available, ...(own as unknown as RolePermissionModel[])]
  }, [available, companyRoles.data])
  const [anchor, setAnchor] = React.useState<{ top: number; left: number } | null>(
    null,
  )
  const ref = React.useRef<HTMLDivElement>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)

  useClickOutside({ ref, handler: () => setOpen(false) })

  // Lista leci portalem na `fixed`, bo kontener tabeli ma `overflow: auto`
  // i przycinał ją tuż pod wierszem.
  const toggleOpen = () => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect) setAnchor({ top: rect.bottom + 4, left: rect.left })
    setOpen((value) => !value)
  }

  return (
    <div className="tw-relative tw-flex tw-items-center tw-gap-2">
      <div className="tw-flex tw-flex-wrap tw-gap-1">
        {selected.map((role) => (
          <button
            key={role.id}
            type="button"
            onClick={() => onToggle(role)}
            className="tw-flex tw-items-center tw-gap-1 tw-rounded tw-border tw-border-primary tw-bg-primary/10 tw-px-2 tw-py-0.5 tw-text-xs tw-text-primary"
          >
            {role.name}
            <AdminIcon name="close" className="tw-h-3 tw-w-3" />
          </button>
        ))}
      </div>

      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        className="tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded tw-bg-primary tw-text-white"
      >
        <AdminIcon name="add" className="white tw-h-3 tw-w-3" />
      </button>

      {open &&
        anchor &&
        createPortal(
          <div
            ref={ref}
            style={{ top: anchor.top, left: anchor.left }}
            className="tw-fixed tw-z-[1010] tw-max-h-64 tw-w-64 tw-overflow-y-auto tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-shadow-lg"
          >
          {options.map((role) => {
            const isSelected = selected.some((item) => item.id === role.id)
            return (
              <button
                key={role.id}
                type="button"
                onClick={() => onToggle(role)}
                className="tw-flex tw-w-full tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-text-left tw-text-sm hover:tw-bg-base-200"
              >
                {role.name}
                {isSelected && <AdminIcon name="check" className="tw-h-4 tw-w-4" />}
              </button>
            )
          })}
          </div>,
          document.body,
        )}
    </div>
  )
}
